import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Printer, Undo2 } from 'lucide-react';
import { getInvoice } from '../../api/invoicing';
import { getInvoiceTemplate } from '../../api/platformBranding';
import { openInvoiceWindow, renderInvoiceIntoWindow } from '../../lib/invoiceTemplate';
import { useAuth } from '../../auth/useAuth';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { IssueCreditNoteModal } from './IssueCreditNoteModal';

function money(value: string) {
  return `£${Number(value).toFixed(2)}`;
}

export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false);
  const invoiceQuery = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId!),
    enabled: !!invoiceId,
  });

  // The template is fetched lazily on click rather than up front (it rarely
  // changes, no reason to hold every invoice viewer's browser hostage to
  // it). The window itself is opened synchronously, before the await, so
  // strict pop-up blockers (Safari especially) still treat this as a
  // direct user gesture rather than blocking it.
  async function handlePrint() {
    if (!invoiceQuery.data) return;
    try {
      const win = openInvoiceWindow();
      const template = await getInvoiceTemplate();
      renderInvoiceIntoWindow(win, invoiceQuery.data, template);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not open the print window.', 'error');
    }
  }

  if (!invoiceId) return null;

  if (invoiceQuery.isLoading || !invoiceQuery.data) {
    return (
      <div className="py-14 text-center">
        <Spinner />
      </div>
    );
  }

  const invoice = invoiceQuery.data;

  return (
    <div>
      <PageHeader
        breadcrumb={
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Link to="/invoices" className="hover:text-slate-600">
              Invoices
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{invoice.customerOrganisation?.name}</span>
          </div>
        }
        title={`Invoice ${invoice.invoiceNumber} - ${invoice.customerOrganisation?.name ?? ''}`}
        description={`${new Date(invoice.billingPeriodStart).toLocaleDateString()} – ${new Date(invoice.billingPeriodEnd).toLocaleDateString()}`}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={handlePrint}>
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </Button>
            {user?.isMspAdmin && (
              <Button size="sm" variant="secondary" onClick={() => setCreditNoteModalOpen(true)}>
                <Undo2 className="h-3.5 w-3.5" /> Issue credit note
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <Table>
          <Thead>
            <tr>
              <Th>Description</Th>
              <Th>Site</Th>
              <Th>Net</Th>
              <Th>VAT</Th>
              <Th>Total</Th>
            </tr>
          </Thead>
          <tbody>
            {invoice.lines?.map((line) => (
              <Tr key={line.id}>
                <Td className="font-medium text-slate-900">{line.description}</Td>
                <Td>{line.site?.name ?? '-'}</Td>
                <Td>{money(line.netAmount)}</Td>
                <Td>{money(line.vatAmount)}</Td>
                <Td>{money(String(Number(line.netAmount) + Number(line.vatAmount)))}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <CardBody className="flex flex-col items-end gap-1 border-t border-slate-100 text-sm">
          <div className="flex w-48 justify-between">
            <span className="text-slate-500">Subtotal</span>
            <span className="text-slate-800">{money(invoice.subtotal)}</span>
          </div>
          <div className="flex w-48 justify-between">
            <span className="text-slate-500">VAT</span>
            <span className="text-slate-800">{money(invoice.vatTotal)}</span>
          </div>
          <div className="flex w-48 justify-between border-t border-slate-100 pt-1 font-semibold text-slate-900">
            <span>Total</span>
            <span>{money(invoice.grandTotal)}</span>
          </div>
        </CardBody>
      </Card>

      <IssueCreditNoteModal
        orgId={invoice.customerOrganisationId}
        invoiceId={invoice.id}
        open={creditNoteModalOpen}
        onClose={() => setCreditNoteModalOpen(false)}
      />
    </div>
  );
}
